import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Grid, Typography, Button, Tabs, Tab, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import muiCheckboxStyles from '../../../uiComponents/MuiInput/MuiCheckboxStyles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import PatientDetailsBiochemicalAnalyteTable from './PatientDetailsBiochemicalAnalyteTable';
import PatientDetailsClirRiskTable from './PatientDetailsClirRiskTable';
import PatientAbnormalBiomarkersConditionsTable from './PatientAbnormalBiomarkersConditionsTable';  
import { exportToCSV } from '../../../utils/ExportToCSV';
import { stableSort, getComparator } from '../../../uiComponents/MuiTable/MuiHelper';
import sanitizeForFilename from '../../../utils/sanitizeForFilename';
import { FileDownload, CandlestickChart } from '@mui/icons-material';
import BoxplotModal from '../../BoxPlotModal';
import APIUtils from '../../../utils/APIUtils';
import './index.css';

const PatientDetailsBiochemical = ({ 
  biochemicalData,
  selectedAnalyte,
  setSelectedAnalyte,
  biochemicalStatistics,
  programId,
  classes,
  muiTabClasses,
  onFetchPlotOptionsData,
  selectedValue,
  patientId
}) => {

  const getTableDataWithDescriptions = (analyteArray, statistics) => {
    if (!Array.isArray(analyteArray) || !statistics?.statistics) return [];
    return analyteArray.map(analyte => {
      const stat = statistics.statistics.find(s => s.analyte_code === analyte.code);
      return {
        ...analyte,
        code: stat?.descAndShortName || analyte.code, // Replace code with descAndShortName if found
      };
    });
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [flaggedSort, setFlaggedSort] = useState(false);
  const [tableSortOrder, setTableSortOrder] = useState('asc');
  const [tableSortBy, setTableSortBy] = useState('code');
  const checkboxClasses = muiCheckboxStyles();

  const tableData = biochemicalData?.analyteData
    ? getTableDataWithDescriptions(biochemicalData.analyteData, biochemicalStatistics)
    : [];

  const ageHr = biochemicalData?.ageHr;
  // console.log('abnormal', JSON.stringify(biochemicalData?.abnormalAnalyteData));
  const tableDataClirRisk = biochemicalData?.clirRiskData?.toolResults || [];
  const sharedClir = biochemicalData?.sharedClir === true;

  // Check if there are any flagged conditions
  const hasFlaggedConditions = useMemo(() => {
    return tableDataClirRisk.some(r => r.isInformative === true);
  }, [tableDataClirRisk]);

  const sortedData = useMemo(() => {
    if (flaggedSort) {
      const flagged = tableDataClirRisk.filter(r => r.isInformative === true)
        .sort((a, b) => (a.conditionName || '').localeCompare(b.conditionName || ''));
      const unflagged = tableDataClirRisk.filter(r => r.isInformative !== true)
        .sort((a, b) => (a.conditionName || '').localeCompare(b.conditionName || ''));
      return [...flagged, ...unflagged];
    }
    return tableDataClirRisk;
  }, [tableDataClirRisk, flaggedSort]);

  // Create box plot chart data
  const boxPlotChartData = useMemo(() => {
    const analyteArray = biochemicalData?.analyteData || [];
    if (!Array.isArray(analyteArray) || !biochemicalStatistics?.statistics) return [];
    return analyteArray
      .map(analyte => {
        const stat = biochemicalStatistics.statistics.find(
          s => s.analyte_code === analyte.code && s.program_id === programId 
        );
        if (!stat) return null;
        // Round all numeric fields to 2 decimal places
        const round2 = v => v !== undefined && v !== null ? Number(v.toFixed(2)) : v;
        
        // zscore values for calculating IQR if needed
        const q1 = stat.q1_zscore;
        const q3 = stat.q3_zscore;
        const median = stat.median_zscore;
        
        // Use rendered values from API if available, otherwise calculate using IQR formula
        let rendered_min, rendered_max;
        if (stat.rendered_min_zscore != null && stat.rendered_max_zscore != null) {
          // Use backend-calculated values
          rendered_min = round2(stat.rendered_min_zscore);
          rendered_max = round2(stat.rendered_max_zscore);
        } else {
          // Fallback: Calculate on frontend using IQR formula
          const iqr = (q3 - q1) * 1.5;
          rendered_min = round2(median - iqr);
          rendered_max = round2(median + iqr);
        }
        
        return {
          q1: round2(q1),
          q3: round2(q3),
          max: round2(stat.max_zscore),
          min: round2(stat.min_zscore),
          median: round2(median),
          rendered_min: rendered_min,
          rendered_max: rendered_max,
          patientId: biochemicalData.patientId,
          programId: stat.program_id,
          shortName: stat.shortName,
          descAndShortName: stat.descAndShortName,
          zScore: analyte.zScore !== undefined ? round2(analyte.zScore) : undefined,
        };
      })
      .filter(Boolean);
  }, [biochemicalData, biochemicalStatistics, programId]);

  const csvHeaders = [
    { label: 'Analyte Name', key: 'code' },
    { label: 'Test Result', key: 'value' },
    { label: 'Z-score', key: 'zScore' },
    { label: 'Instrument', key: 'instrument' },
    { label: 'Reagent Kit', key: 'reagentKit' },
    { label: 'Lot Number', key: 'lotNumber' },
    { label: 'Flags', key: 'flaggedStatus' }
  ];

  // Sort tableData using the same sort state as the table for WYSIWYG export
  const sortedTableData = stableSort([...tableData], getComparator(tableSortOrder, tableSortBy));

  // Helper function to convert flaggedStatus to readable text
  const getFlagSeverityText = (row) => {
    if (!row.valueStatus || row.valueStatus !== 'Abnormal' || !row.flaggedStatus) {
      return '';
    }
    const status = (row.flaggedStatus || '').toLowerCase();
    if (status === 'strong') return 'High severity';
    if (status === 'moderate') return 'Medium severity';
    if (status === 'mild') return 'Low severity';
    return '';
  };

  // Only include keys defined in csvHeaders
  const formattedCsvData = sortedTableData.map(row =>
    Object.fromEntries(
      csvHeaders.map(header => [
        header.key,
        header.key === 'value' || header.key === 'zScore'
          ? (row[header.key] !== null && row[header.key] !== undefined && 
            !isNaN(Number(row[header.key])) ? Number(row[header.key]).toFixed(2) : 'N/A')
          : header.key === 'flaggedStatus'
          ? getFlagSeverityText(row)
          : row[header.key]
      ])
    )
  );

  // Function to fetch and format statistics for Plot Options
  const handleFetchPlotOptionsData = async (programIds) => {
    if (!onFetchPlotOptionsData) return [];
    
    // programIds will be either the specific programId or 'ALL_PROGRAMS'
    const targetProgramId = programIds;
    
    // Fetch new statistics from parent with the target program
    const response = await onFetchPlotOptionsData(targetProgramId, null);
    
    const analyteArray = biochemicalData?.analyteData || [];
    if (!Array.isArray(analyteArray) || !response?.statistics) return [];
    
    return analyteArray
      .map(analyte => {
        const stat = response.statistics.find(
          s => s.analyte_code === analyte.code && s.program_id === targetProgramId
        );
        if (!stat) return null;
        
        const round2 = v => v !== undefined && v !== null ? Number(v.toFixed(2)) : v;
        
        const q1 = stat.q1_zscore;
        const q3 = stat.q3_zscore;
        const median = stat.median_zscore;
        
        // Use rendered values from API if available, otherwise calculate using IQR formula
        let rendered_min, rendered_max;
        if (stat.rendered_min_zscore != null && stat.rendered_max_zscore != null) {
          // Use backend-calculated values
          rendered_min = round2(stat.rendered_min_zscore);
          rendered_max = round2(stat.rendered_max_zscore);
        } else {
          // Fallback: Calculate on frontend using IQR formula
          const iqr = (q3 - q1) * 1.5;
          rendered_min = round2(median - iqr);
          rendered_max = round2(median + iqr);
        }
        
        return {
          q1: round2(q1),
          q3: round2(q3),
          max: round2(stat.max_zscore),
          min: round2(stat.min_zscore),
          median: round2(median),
          rendered_min: rendered_min,
          rendered_max: rendered_max,
          patientId: biochemicalData.patientId,
          programId: stat.program_id,
          shortName: stat.shortName,
          descAndShortName: stat.descAndShortName,
          zScore: analyte.zScore !== undefined ? round2(analyte.zScore) : undefined,
        };
      })
      .filter(Boolean);
  };

  // Function to fetch boxplot data for a specific test result
  const handleFetchWithTestResult = async (testId, dataScope) => {
    if (!onFetchPlotOptionsData) return [];
    
    // Determine which program to fetch based on data scope
    const targetProgramId = dataScope === 'all_programs' ? 'ALL_PROGRAMS' : programId;
    
    // Fetch biochemical data for the selected test result using APIUtils
    const testResultData = await APIUtils.getBiochemicalDataByProgramPatientId(
      programId, 
      patientId, 
      testId
    );
    
    // Fetch statistics for the target program
    const statisticsResponse = await onFetchPlotOptionsData(targetProgramId, null);
    
    // Format the data using the same logic as the initial boxplot
    const analyteArray = testResultData?.analyteData || [];
    if (!Array.isArray(analyteArray) || !statisticsResponse?.statistics) return [];
    
    return analyteArray
      .map(analyte => {
        const stat = statisticsResponse.statistics.find(
          s => s.analyte_code === analyte.code && s.program_id === targetProgramId
        );
        if (!stat) return null;
        
        const round2 = v => v !== undefined && v !== null ? Number(v.toFixed(2)) : v;
        
        const q1 = stat.q1_zscore;
        const q3 = stat.q3_zscore;
        const median = stat.median_zscore;
        
        let rendered_min, rendered_max;
        if (stat.rendered_min_zscore != null && stat.rendered_max_zscore != null) {
          rendered_min = round2(stat.rendered_min_zscore);
          rendered_max = round2(stat.rendered_max_zscore);
        } else {
          const iqr = (q3 - q1) * 1.5;
          rendered_min = round2(median - iqr);
          rendered_max = round2(median + iqr);
        }
        
        return {
          q1: round2(q1),
          q3: round2(q3),
          max: round2(stat.max_zscore),
          min: round2(stat.min_zscore),
          median: round2(median),
          rendered_min: rendered_min,
          rendered_max: rendered_max,
          patientId: testResultData.patientId,
          programId: stat.program_id,
          shortName: stat.shortName,
          descAndShortName: stat.descAndShortName,
          zScore: analyte.zScore !== undefined ? round2(analyte.zScore) : undefined,
        };
      })
      .filter(Boolean);
  };

  // Memoize the sort change handler to prevent unnecessary re-renders
  const handleSortChange = useCallback((order, orderBy) => {
    setTableSortOrder(order);
    setTableSortBy(orderBy);
  }, []);

  return (
    <>
      {biochemicalData && (
        <Box mt={2}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            className={muiTabClasses.tabs}
            sx={{ mb: 2 }}
          >
            <Tab label="Test Results" className={muiTabClasses.tab} />
            <Tab label="CLIR Risk Scores" className={muiTabClasses.tab} />
            <Tab label="Abnormal Biomarkers & Potential Conditions" className={muiTabClasses.tab} />
          </Tabs>
          {selectedTab === 0 && (
            <>
            <Typography variant="body1" gutterBottom>
              Age at collection:&nbsp;
              {
                ageHr != null
                  ? `${ageHr} hours`
                  : <em>Not Provided</em>
              }
            </Typography>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} sx={{ my: 1 }}>
                <Autocomplete
                  fullWidth
                  className={classes.muiInputs}
                  options={[...tableData.map(row => row.code)].sort((a, b) => a.localeCompare(b))}
                  value={selectedAnalyte}
                  onChange={(event, newValue) => setSelectedAnalyte(newValue)}
                  renderInput={(params) => <TextField {...params} label="Select Analyte" variant="outlined" size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} textAlign="right">
                <Button 
                  variant="outlined" 
                  onClick={() =>
                    exportToCSV(
                      formattedCsvData,
                      `ED3N_test_results_${sanitizeForFilename(biochemicalData?.testResultViews?.[0]?.testName || 'test_result')}.csv`,
                      csvHeaders,
                      ['value', 'zScore']
                    )
                  } 
                  startIcon={<FileDownload />}
                >
                  Export
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => setShowModal(true)} 
                  sx={{ ml: 1 }} 
                  startIcon={<CandlestickChart />}
                >
                  View Patient Boxplot
                </Button>
              </Grid>
            </Grid>
            <Box mt={1}>
              <PatientDetailsBiochemicalAnalyteTable
                data={tableData}
                selectedAnalyte={selectedAnalyte}
                onSortChange={handleSortChange}
              />
            </Box>
            </>
          )}
          {selectedTab === 1 && (
            <>
            {sharedClir ? (
            <Box mb={2} maxWidth={400} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    className={checkboxClasses.checkboxRoot}
                    checked={flaggedSort}
                    onChange={e => setFlaggedSort(e.target.checked)}
                    disabled={!hasFlaggedConditions}
                    inputProps={{
                      'aria-label': 'Show CLIR Flagged Conditions First',
                      'aria-disabled': !hasFlaggedConditions,
                      'aria-describedby': !hasFlaggedConditions ? 'clir-flagged-info' : undefined,
                    }}
                  />
                }
                label="Show CLIR Flagged Conditions First"
                sx={!hasFlaggedConditions ? { cursor: 'not-allowed', opacity: 0.38 } : {}}
              />
              {!hasFlaggedConditions && (
                <Tooltip title="No flagged condition data" placement="top" arrow>
                  <InfoOutlinedIcon
                    id="clir-flagged-info"
                    tabIndex={0}
                    role="button"
                    aria-label="No flagged condition data"
                    aria-hidden={false}
                    style={{
                      fontSize: '1.1rem',
                      color: 'var(--info-color)',
                      cursor: 'pointer',
                      marginLeft: '4px',
                      verticalAlign: 'middle',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            ) : (
              <Typography variant="body1" mb={2}>
                CLIR scores were not calculated for this patient based on your program settings
              </Typography>
            )}
            {sharedClir && (
              <Box mt={2}>
                <PatientDetailsClirRiskTable 
                  data={sortedData} 
                  flaggedSort={flaggedSort}
                />
              </Box>
            )}
            </>
          )}
          {selectedTab === 2 && (
            <>
              <Box mt={2}>
               <PatientAbnormalBiomarkersConditionsTable
                  data={biochemicalData?.abnormalAnalyteData}/>
              </Box>
            </>
          )}
        </Box>
      )}
      
      {boxPlotChartData.length > 0 && (
        <BoxplotModal
          open={showModal}
          onClose={() => setShowModal(false)}
          data={boxPlotChartData}
          title={`Patient Analyte Comparison Plot: Patient ID ${biochemicalData.patientId}`}
          xAxisLabel="Analyte Name"
          yAxisLabel="Z-Score"
          xAxisKey="shortName"
          isPatientBiochemData={true}
          showZScore={true}
          allStatistics={biochemicalStatistics?.statistics}
          selectedAnalyte={selectedAnalyte}
          programOptions={biochemicalStatistics?.statistics ? 
            [...new Set(biochemicalStatistics.statistics.map(s => s.program_id))] : []}
          userProgramId={programId}
          patientData={biochemicalData?.analyteData}
          onFetchStatistics={handleFetchPlotOptionsData}
          initialDataScope="my_program"
          testResultOptions={biochemicalData?.testResultViews?.map(view => ({
            label: view.testName,
            testId: view.testId,
            active: view.active
          })) || []}
          initialTestResult={selectedValue}
          onFetchWithTestResult={handleFetchWithTestResult}
          minWidthPercent={85}
        />
      )}
    </>
  );
};

export default PatientDetailsBiochemical;